import { Injectable } from '@angular/core';

/**
 * Lazy-loads Three.js + cannon-es + the bowling pin GLB only when a bowling
 * game starts so the ~200 KB gzipped 3D payload (and the ~12 MB pin model)
 * stays out of the main bundle and is fetched only for users who enter this
 * specific mini-game.
 *
 * Do NOT import from 'three' or 'cannon-es' at module scope anywhere in the
 * codebase — that would defeat the async split. Always route through this
 * service or use `typeof import(...)` for types only.
 */
@Injectable({ providedIn: 'root' })
export class BowlingEngineService {
  /** Cached `three` module — `import('three')` returns the same instance
   *  on subsequent calls but caching saves the ESM resolution cost. */
  private threeMod: typeof import('three') | null = null;
  /** Cached `cannon-es` module — same caching reasoning as `threeMod`. */
  private cannonMod: typeof import('cannon-es') | null = null;
  /** Normalised pin Mesh template — set once after the GLB loads + bake
   *  pipeline runs. `loadPinModel` returns a fresh `clone(true)` each
   *  call so each pin instance gets its own scene-graph node. */
  private pinModelTemplate: any = null;
  /** In-flight pin-load promise — guards against parallel duplicate
   *  loads if the scene component re-mounts while the first load is
   *  still pending. */
  private pinModelPromise: Promise<any> | null = null;
  /** Normalised ball Mesh template — same pattern as `pinModelTemplate`
   *  but for `ball.glb`. */
  private ballModelTemplate: any = null;
  /** In-flight ball-load promise. */
  private ballModelPromise: Promise<any> | null = null;

  /**
   * Lazy-import + cache the `three` and `cannon-es` modules. First
   * call dynamically imports both (triggering the webpack chunk
   * split); subsequent calls return the cached refs synchronously.
   * Always `await` the promise — never read `threeMod` directly.
   */
  async load(): Promise<{
    THREE: typeof import('three');
    CANNON: typeof import('cannon-es');
  }> {
    if (!this.threeMod) this.threeMod = await import('three');
    if (!this.cannonMod) this.cannonMod = await import('cannon-es');
    return { THREE: this.threeMod, CANNON: this.cannonMod };
  }

  /**
   * Loads `assets/game-mode/bowling/pin.glb` once, **fully normalises** the
   * geometry into a unit-template (1 m tall, centred at origin in all three
   * axes, +Y up), and returns a fresh deep clone on every call.
   *
   * Why bake everything into the geometry rather than apply transforms on
   * the mesh:
   *   1. **Rotation must be baked** — if the up-axis correction lives on
   *      `mesh.rotation`, the body's runtime rotation (when a pin falls)
   *      compounds with it, flipping the pin 180°.
   *   2. **Centring + scaling baked too** — eliminates per-instance bbox
   *      calls in the scene component. Three.js's bbox caching has bitten
   *      us: a stale `geometry.boundingBox` after `applyMatrix4` left the
   *      pin half-buried in the lane. Doing all math once on the vertex
   *      buffer dodges that whole class of bug.
   *
   * After this runs, the scene component's `buildPinVisual` becomes a
   * one-liner: clone the template, set `scale = PIN_HEIGHT`, parent to a
   * group at the body's centre. The pin's bbox-bottom will sit at exactly
   * `body.y - PIN_HEIGHT/2` because the geometry is centred at origin.
   *
   * Throws on load failure — the scene component catches and falls back to
   * the procedural LatheGeometry pin so the game keeps working.
   */
  async loadPinModel(): Promise<any> {
    if (this.pinModelTemplate) return this.pinModelTemplate.clone(true);
    if (!this.pinModelPromise) {
      this.pinModelPromise = (async () => {
        const { THREE } = await this.load();
        // Loader path cast to any to dodge a broken d.ts in @types/three
        // (Color4.d.ts has an illegal `.ts` import extension that TS
        // resolves transitively from the loader path). Runtime is normal.
        const loaderMod: any = await import(
          /* webpackChunkName: "three-gltf-loader" */
          'three/examples/jsm/loaders/GLTFLoader.js' as any
        );
        const loader = new loaderMod.GLTFLoader();
        const gltf: any = await loader.loadAsync('assets/game-mode/bowling/pin.glb');
        const root: any = gltf.scene;

        // Find the first mesh — the asset bundles 3 pin variants, any one
        // works as a template since they're identically shaped.
        let firstMesh: any = null;
        root.traverse((obj: any) => {
          if (!firstMesh && obj.isMesh) firstMesh = obj;
        });
        if (!firstMesh) throw new Error('pin.glb contained no mesh');

        // Clone the geometry — the source asset must not be mutated, and we
        // don't want all 3 pin variants in the GLB to silently change shape.
        const geom = firstMesh.geometry.clone();
        const Vector3: any = (THREE as any).Vector3;
        const Matrix4: any = (THREE as any).Matrix4;

        // ── Step 1: rotate so the longest axis aligns with +Y ──────────────
        geom.computeBoundingBox();
        const naturalSize = geom.boundingBox.getSize(new Vector3());
        let rotMat: any = null;
        if (naturalSize.z >= naturalSize.y && naturalSize.z >= naturalSize.x) {
          rotMat = new Matrix4().makeRotationX(-Math.PI / 2);   // Z-up → Y-up
        } else if (naturalSize.x > naturalSize.y && naturalSize.x > naturalSize.z) {
          rotMat = new Matrix4().makeRotationZ(Math.PI / 2);    // X-up → Y-up
        }
        if (rotMat) geom.applyMatrix4(rotMat);

        // ── Step 2: translate so the bbox is centred at origin in all 3
        //           axes. Without this, an asymmetric vertex range (this
        //           model: post-bake y range [-3.19, +6.45]) leaves the
        //           visible base offset from where the body's centre sits.
        geom.computeBoundingBox();
        const cx = (geom.boundingBox.min.x + geom.boundingBox.max.x) / 2;
        const cy = (geom.boundingBox.min.y + geom.boundingBox.max.y) / 2;
        const cz = (geom.boundingBox.min.z + geom.boundingBox.max.z) / 2;
        geom.applyMatrix4(new Matrix4().makeTranslation(-cx, -cy, -cz));

        // ── Step 3: scale uniformly so the Y dimension is exactly 1 unit.
        //           Per-instance scaling (PIN_HEIGHT) is then a single
        //           multiplication, no bbox math needed in the scene.
        geom.computeBoundingBox();
        const sizeY = geom.boundingBox.max.y - geom.boundingBox.min.y;
        if (sizeY > 0) {
          const s = 1 / sizeY;
          geom.applyMatrix4(new Matrix4().makeScale(s, s, s));
        }
        geom.computeBoundingBox();
        // Post-condition: bbox.y in [-0.5, +0.5], bbox.x and bbox.z centred
        // at 0. Width/depth are still proportional to the model.

        firstMesh.geometry = geom;
        // Reset transform on the mesh so clones start at identity.
        firstMesh.position.set(0, 0, 0);
        firstMesh.rotation.set(0, 0, 0);
        firstMesh.scale.set(1, 1, 1);
        firstMesh.updateMatrixWorld(true);

        return firstMesh;
      })();
    }
    this.pinModelTemplate = await this.pinModelPromise;
    return this.pinModelTemplate.clone(true);
  }

  /**
   * Loads `assets/game-mode/bowling/ball.glb` once, normalises the
   * geometry into a unit-diameter sphere centred at origin, and
   * returns a fresh clone on every call.
   *
   * Same baking-into-geometry approach as `loadPinModel` — every
   * transform lives in the vertex buffer, the runtime mesh stays at
   * identity. No rotation step is needed (a sphere has no preferred
   * axis), but centre + uniform scale are baked so per-instance
   * sizing is a single `mesh.scale = BALL_DIAMETER` in the scene.
   *
   * Throws on load failure — the scene component catches and falls
   * back to the procedural sphere material so the game keeps working.
   */
  async loadBallModel(): Promise<any> {
    if (this.ballModelTemplate) return this.ballModelTemplate.clone(true);
    if (!this.ballModelPromise) {
      this.ballModelPromise = (async () => {
        const { THREE } = await this.load();
        const loaderMod: any = await import(
          /* webpackChunkName: "three-gltf-loader" */
          'three/examples/jsm/loaders/GLTFLoader.js' as any
        );
        const loader = new loaderMod.GLTFLoader();
        const gltf: any = await loader.loadAsync('assets/game-mode/bowling/ball.glb');
        const root: any = gltf.scene;

        // First mesh — this asset bundles a single ball model.
        let firstMesh: any = null;
        root.traverse((obj: any) => {
          if (!firstMesh && obj.isMesh) firstMesh = obj;
        });
        if (!firstMesh) throw new Error('ball.glb contained no mesh');

        // Clone the geometry so the source asset stays untouched.
        const geom = firstMesh.geometry.clone();
        const Vector3: any = (THREE as any).Vector3;
        const Matrix4: any = (THREE as any).Matrix4;

        // ── Step 1: translate so the bbox is centred at origin. The ball
        //           is roughly spherical, but the asset may have a few-cm
        //           offset that would otherwise show up as the visible
        //           sphere being off-centre from the physics body.
        geom.computeBoundingBox();
        const cx = (geom.boundingBox.min.x + geom.boundingBox.max.x) / 2;
        const cy = (geom.boundingBox.min.y + geom.boundingBox.max.y) / 2;
        const cz = (geom.boundingBox.min.z + geom.boundingBox.max.z) / 2;
        geom.applyMatrix4(new Matrix4().makeTranslation(-cx, -cy, -cz));

        // ── Step 2: scale uniformly so the longest axis is exactly 1 unit.
        //           Per-instance scaling (BALL_DIAMETER) is then a single
        //           `mesh.scale.setScalar(...)` in the scene.
        geom.computeBoundingBox();
        const size = geom.boundingBox.getSize(new Vector3());
        const longest = Math.max(size.x, size.y, size.z);
        if (longest > 0) {
          const s = 1 / longest;
          geom.applyMatrix4(new Matrix4().makeScale(s, s, s));
        }
        geom.computeBoundingBox();
        geom.computeBoundingSphere();

        firstMesh.geometry = geom;
        // Reset mesh transform so clones start at identity.
        firstMesh.position.set(0, 0, 0);
        firstMesh.rotation.set(0, 0, 0);
        firstMesh.scale.set(1, 1, 1);
        firstMesh.castShadow = true;

        // Encourage proper PBR rendering of the embedded textures.
        const mat: any = firstMesh.material;
        if (mat && (THREE as any).SRGBColorSpace) {
          if (mat.map) {
            mat.map.colorSpace = (THREE as any).SRGBColorSpace;
          }
          // normalMap / roughnessMap / metalnessMap stay in linear space.
          mat.needsUpdate = true;
        }

        firstMesh.updateMatrixWorld(true);
        return firstMesh;
      })();
    }
    this.ballModelTemplate = await this.ballModelPromise;
    return this.ballModelTemplate.clone(true);
  }
}
